import React, { useState, useEffect } from 'react';
import { Eye, CheckCircle2, Lightbulb, Loader2, ChevronRight, AlertCircle, RefreshCw, Sparkles, SkipForward, ArrowLeft } from 'lucide-react';
import { useFamilyContext } from '../../../contexts/FamilyContext';
import { collegeJourneyService } from '../../../lib/college-journey-service';
import EssayResultsDisplay from './EssayResultsDisplay';

export default function MomentsThatMatterInterview({ onComplete, assessmentData }) {
  const { currentUser } = useFamilyContext();
  const [phase, setPhase] = useState('intro');
  const [responses, setResponses] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [insights, setInsights] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [savedResponses, setSavedResponses] = useState(null);
  const [showAutoSave, setShowAutoSave] = useState(false);

  const questions = [
    {
      id: 'dinner_table',
      question: "Describe the last dinner conversation you had where someone made a comment that made you think differently about something.",
      placeholder: "What were you eating? Who said it? What exact words do you remember?"
    },
    {
      id: 'overheard',
      question: "What's a conversation you overheard (not meant for you) that stuck with you?",
      placeholder: "Where were you? What were they saying? Why did you keep listening?"
    },
    {
      id: 'found_object',
      question: "Describe something you found (not bought) in the last year that meant something to you.",
      placeholder: "Where did you find it? What does it look like? Why did you keep it?"
    },
    {
      id: 'wait_moment',
      question: "Describe a time in the last few months when you were waiting for something and noticed something you wouldn't normally notice.",
      placeholder: "What were you waiting for? What caught your attention? What did you think about?"
    },
    {
      id: 'music_moment',
      question: "What song were you listening to the last time you felt completely understood?",
      placeholder: "Where were you? What was happening? What lyric hit you?"
    },
    {
      id: 'text_thread',
      question: "What's a text message or conversation thread you've re-read more than once? Why do you go back to it?",
      placeholder: "Who was it with? What was the context? What specific message matters?"
    },
    {
      id: 'drive_walk',
      question: "Describe the last time you were driving/walking somewhere and completely lost in thought. What were you thinking about?",
      placeholder: "Where were you going? What triggered the thought? Where did it lead?"
    },
    {
      id: 'helping_hands',
      question: "Describe the last time you watched someone else's hands doing something - cooking, fixing, creating, working. What did you notice?",
      placeholder: "Whose hands? What were they doing? What did their movements tell you?"
    }
  ];

  const currentQuestion = questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;
  const wordCount = currentAnswer.trim().split(/\s+/).filter(Boolean).length;
  const hasMinimumDetail = wordCount >= 50;

  // Auto-save indicator effect
  useEffect(() => {
    if (currentAnswer.length > 0) {
      setShowAutoSave(true);
      const timer = setTimeout(() => setShowAutoSave(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [currentAnswer]);

  const startInterview = () => {
    setPhase('questioning');
  };

  const submitAnswer = () => {
    const newResponses = {
      ...responses,
      [currentQuestion.id]: currentAnswer.trim() || ''
    };
    setResponses(newResponses);

    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setCurrentAnswer('');
    } else {
      finishInterview(newResponses);
    }
  };

  const skipQuestion = () => {
    const newResponses = {
      ...responses,
      [currentQuestion.id]: ''
    };
    setResponses(newResponses);

    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setCurrentAnswer('');
    } else {
      finishInterview(newResponses);
    }
  };

  const finishInterview = async (finalResponses) => {
    const answeredQuestions = Object.values(finalResponses).filter(
      response => response && response.trim().length > 0
    );
    
    if (answeredQuestions.length < 4) {
      alert(`Please answer at least 4 questions to complete the interview. You've answered ${answeredQuestions.length} questions.`);
      return;
    }

    setPhase('processing');
    setIsProcessing(true);

    try {
      const insightData = await generateInsights(finalResponses);
      setInsights(insightData);
      
      try {
        console.log('💾 Saving interview results to database...');
        const saveResult = await collegeJourneyService.saveInterviewResults(
          currentUser?.id,
          'moments_that_matter',
          insightData
        );
        console.log('💾 Interview results saved:', saveResult ? 'Success' : 'Failed (table may not exist)');
      } catch (saveError) {
        console.error('Failed to save interview results:', saveError);
      }
      
      setPhase('results');
      
      if (onComplete) {
        onComplete({
          type: 'moments_that_matter',
          responses: finalResponses,
          insights: insightData
        });
      }
    } catch (error) {
      console.error('Error generating insights:', error);
      
      setSavedResponses(finalResponses);
      
      const isParsingError = error.message?.includes('parse') || 
                            error.message?.includes('JSON') ||
                            (error.shouldRetry);
      
      setInsights({
        error: true,
        message: error.isOverloaded 
          ? "The AI service is temporarily busy. Please try again in a moment."
          : isParsingError
          ? "The AI response format was unexpected. Please try again."
          : "Failed to generate insights. Please try again.",
        canRetry: true
      });
      setPhase('error');
    }

    setIsProcessing(false);
  };

  const generateInsights = async (interviewResponses) => {
    try {
      const response = await fetch('/api/essay-ideas/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          interviewType: 'moments_that_matter',
          studentName: currentUser?.name || currentUser?.displayName || 'this student',
          responses: interviewResponses,
          userId: currentUser?.id,
          context: {
            personality: assessmentData?.personality,
            assessmentCompleted: !!assessmentData
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();

        if (errorData.isOverloaded || response.status === 503) {
          const error = new Error('Service temporarily overloaded');
          error.isOverloaded = true;
          throw error;
        }

        throw new Error(errorData.error || 'Failed to generate insights');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error calling insights API:', error);
      
      error.isNetworkError = error.message.includes('fetch') || error.message.includes('network');
      error.isOverloaded = error.isOverloaded || error.message.includes('overloaded');
      
      throw error;
    }
  };

  const retryAnalysis = async () => {
    setPhase('processing');
    setIsProcessing(true);
    setRetryCount(prev => prev + 1);
    
    try {
      const responsesToAnalyze = savedResponses || responses;
      const insightData = await generateInsights(responsesToAnalyze);
      setInsights(insightData);
      
      try {
        console.log('💾 Saving retry interview results to database...');
        const saveResult = await collegeJourneyService.saveInterviewResults(
          currentUser?.id,
          'moments_that_matter',
          insightData
        );
        console.log('💾 Retry interview results saved:', saveResult ? 'Success' : 'Failed (table may not exist)');
      } catch (saveError) {
        console.error('Failed to save retry interview results:', saveError);
      }
      
      setPhase('results');
      
      if (onComplete) {
        onComplete({
          type: 'moments_that_matter',
          responses: responsesToAnalyze,
          insights: insightData
        });
      }
    } catch (error) {
      console.error(`Retry attempt ${retryCount + 1} failed:`, error);
      
      setInsights({
        error: true,
        message: `Retry attempt ${retryCount + 1} failed. ${
          error.isOverloaded 
            ? "The service is still busy." 
            : "Please try again."
        }`,
        canRetry: true
      });
      setPhase('error');
    }
    
    setIsProcessing(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      submitAnswer();
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 font-['Reddit_Sans',system-ui,sans-serif]">
      {phase === 'intro' && (
        <>
          {/* Header with Back Navigation */}
          <div className="bg-white border-b-2 border-slate-900">
            <div className="max-w-5xl mx-auto px-8 py-6">
              <button 
                onClick={() => window.history.back()}
                className="flex items-center gap-2 text-slate-700 hover:text-slate-900 transition-colors mb-6"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm font-medium">Back to Ideas Hub</span>
              </button>
              
              <div className="flex items-start gap-6">
                <div>
                  <h1 className="text-3xl font-bold text-slate-900 mb-2">Moments That Matter</h1>
                  <p className="text-base text-slate-700">Discover essays in the details you notice</p>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="max-w-5xl mx-auto px-8 py-12">
            {/* Introduction */}
            <div className="mb-12">
              <p className="text-base text-slate-900 leading-relaxed">
                This interview explores the specific moments that have stuck with you - not big achievements, but small observations that reveal how you see the world.
              </p>
            </div>

            {/* What We're Looking For Section */}
            <div className="mb-12 bg-white border-2 border-slate-900 rounded-xl overflow-hidden">
              <div className="px-6 py-4 bg-slate-900 border-l-4 border-l-gold-400">
                <h2 className="text-white font-bold text-sm uppercase tracking-wide">What I'm looking for:</h2>
              </div>
              <div className="p-8 bg-white">
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-2 h-2 rounded-full bg-gold-500 mt-2 flex-shrink-0"></div>
                    <p className="text-base text-slate-900">Specific sensory details from real memories</p>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-2 h-2 rounded-full bg-gold-500 mt-2 flex-shrink-0"></div>
                    <p className="text-base text-slate-900">Ordinary moments that captured your attention</p>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-2 h-2 rounded-full bg-gold-500 mt-2 flex-shrink-0"></div>
                    <p className="text-base text-slate-900">What you notice that others might miss</p>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-2 h-2 rounded-full bg-gold-500 mt-2 flex-shrink-0"></div>
                    <p className="text-base text-slate-900">Conversations or observations that stuck with you</p>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-2 h-2 rounded-full bg-gold-500 mt-2 flex-shrink-0"></div>
                    <p className="text-base text-slate-900">The texture and detail of everyday life</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Important Callout */}
            <div className="mb-12 bg-gold-50 border-2 border-gold-500 rounded-xl p-6 border-l-4 border-l-gold-400">
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-gold-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-slate-900 mb-1">This is not the place for your "impressive" moments.</p>
                  <p className="text-xs text-slate-800 pt-2">Prioritize the memories that are most vivid in your mind. Bring them to life with specific details about what you saw, heard, felt, or thought.</p>
                  <p className="text-xs font-semibold text-slate-800 pt-1">Your job is to take me into the moment; my job is to find the stories that show up between the lines.</p>
                </div>
              </div>
            </div>

            {/* Begin Button */}
            <div>
              <button 
                onClick={startInterview}
                className="px-8 py-4 bg-gold-400 text-slate-900 border-2 border-slate-900 rounded-lg font-bold text-base hover:bg-gold-500 transition-all active:scale-95 shadow-sm"
              >
                <Eye className="inline w-5 h-5 mr-3" />
                Let's Begin
              </button>
            </div>
          </div>
        </>
      )}

      {phase === 'questioning' && currentQuestion && (
        <div className="max-w-4xl mx-auto">
          {/* Progress Header */}
          <div className="bg-white border-2 border-slate-900 rounded-xl shadow-sm overflow-hidden mb-6">
            <div className="bg-slate-900 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 bg-violet-400 rounded-full"></div>
                  <span className="text-sm font-bold text-white">
                    Question {currentQuestionIndex + 1} of {questions.length}
                  </span>
                  <span className="text-stone-400 text-xs">
                    • ~{Math.ceil((questions.length - currentQuestionIndex) * 2)} min remaining
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  {showAutoSave && (
                    <span className="text-xs text-teal-300 animate-fadeIn">Saved</span>
                  )}
                  <span className="text-sm text-stone-300">{Math.round(progress)}% complete</span>
                </div>
              </div>
            </div>
            <div className="relative h-2 bg-stone-200">
              <div 
                className="absolute inset-y-0 left-0 bg-violet-300 transition-all duration-500"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>

          {/* Question Card */}
          <div className="bg-white border border-slate-900 rounded-xl shadow-sm">
            <div className="p-8 pb-6">
              <h1 className="text-base font-semibold text-slate-900 mb-4 leading-relaxed">
                {currentQuestion.question}
              </h1>

              {/* Guidance Callout */}
              <div className="mb-6 bg-violet-50 border-l-4 border-l-violet-600 rounded-r-lg p-4">
                <p className="text-xs font-semibold text-violet-900 mb-1">What makes a great response:</p>
                <p className="text-xs text-slate-900">
                  Include specific details – what you <span className="font-semibold text-violet-900">saw</span>, <span className="font-semibold text-violet-900">heard</span>, <span className="font-semibold text-violet-900">felt</span>, or <span className="font-semibold text-violet-900">thought</span>. The more you provide, the more I can help you spot the non-obvious angles that make this story memorable.
                </p>
              </div>

              {/* Textarea */}
              <div className="relative">
                <textarea
                  value={currentAnswer}
                  onChange={(e) => setCurrentAnswer(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={currentQuestion.placeholder}
                  className="w-full min-h-[280px] p-4 border-2 border-slate-300 rounded-lg text-slate-900 text-sm placeholder:text-stone-500 focus:outline-none focus:border-violet-600 focus:ring-2 focus:ring-violet-100 transition-all resize-none"
                  autoFocus
                />
                
                {wordCount > 0 && (
                  <div className="absolute bottom-3 right-3 flex items-center gap-2">
                    {hasMinimumDetail ? (
                      <div className="flex items-center gap-1.5 text-teal-600 text-xs font-medium bg-teal-50 px-2 py-1 rounded">
                        <div className="w-1 h-1 bg-teal-600 rounded-full"></div>
                        Good detail
                      </div>
                    ) : (
                      <div className="text-stone-500 text-xs">
                        {wordCount} words • Keep going...
                      </div>
                    )}
                  </div>
                )}
              </div>

              {wordCount === 0 && (
                <button className="mt-3 text-xs text-violet-600 hover:text-violet-700 font-medium">
                  Show me an example response →
                </button>
              )}
            </div>

            {/* Footer */}
            <div className="bg-stone-50 px-8 py-5 border-t border-stone-200 rounded-b-xl">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm">
                  <p className="text-stone-600 text-xs flex items-center gap-2">
                    <span className="inline-block w-1 h-1 bg-stone-400 rounded-full"></span>
                    {Object.values(responses).filter(r => r && r.trim().length > 0).length} of 4 minimum questions answered
                    <span className="inline-block w-1 h-1 bg-stone-400 rounded-full"></span>
                    Press Ctrl+Enter to submit
                  </p>
                </div>
                
                <div className="flex gap-3">
                  <button 
                    onClick={skipQuestion}
                    className="px-5 py-2.5 text-slate-700 text-sm font-medium hover:text-slate-900 transition-colors flex items-center gap-1.5"
                  >
                    <SkipForward size={14} />
                    Skip
                  </button>
                  <button 
                    onClick={submitAnswer}
                    className="bg-violet-600 text-white px-6 py-2.5 rounded-lg border-2 border-slate-900 font-medium text-sm hover:bg-violet-700 transition-all active:scale-95 flex items-center gap-2 shadow-sm"
                  >
                    {currentQuestionIndex < questions.length - 1 ? 'Next Question' : 'Complete Interview'}
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>

              {currentQuestionIndex < questions.length - 1 && (
                <div className="pt-3 border-t border-stone-200">
                  <div className="flex items-start gap-2">
                    <div className="w-1 h-1 bg-violet-600 rounded-full mt-1.5 flex-shrink-0"></div>
                    <div className="text-xs text-stone-600">
                      <span className="font-semibold text-slate-700">COMING NEXT:</span> {questions[currentQuestionIndex + 1].question}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {phase === 'processing' && (
        <div className="bg-white rounded-2xl p-8 border border-stone-200 shadow-lg text-center">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-violet-500 to-purple-500 rounded-xl flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-white animate-spin" />
            </div>
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-4">Analyzing Your Observations</h3>
          <p className="text-slate-600 mb-6">
            We're examining the moments you've shared to identify what they reveal about how you see and process the world around you.
          </p>
          <div className="bg-violet-50 border border-violet-200 rounded-lg p-4">
            <p className="text-sm text-violet-700">
              <strong>Please wait...</strong> This analysis reveals the distinctive way you notice details and make meaning from everyday moments.
            </p>
          </div>
        </div>
      )}

      {phase === 'error' && insights?.error && (
        <div className="bg-red-50 rounded-2xl p-8 border border-red-200">
          <div className="text-center">
            <div className="flex items-center justify-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-xl flex items-center justify-center">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">
              Analysis Generation Failed
            </h3>
            <p className="text-red-700 mb-6 text-lg">{insights.message}</p>
            
            <button
              onClick={retryAnalysis}
              disabled={isProcessing}
              className="bg-red-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
            >
              <RefreshCw className={`w-5 h-5 ${isProcessing ? 'animate-spin' : ''}`} />
              {isProcessing ? 'Retrying...' : `Retry Analysis${retryCount > 0 ? ` (Attempt ${retryCount + 1})` : ''}`}
            </button>
            
            <p className="text-sm text-slate-600 mt-4">
              Your responses have been saved and will be reprocessed when you retry.
            </p>
            
            {retryCount > 2 && (
              <p className="text-sm text-amber-600 mt-2">
                If the issue persists, please try again in a few minutes or contact support.
              </p>
            )}
          </div>
        </div>
      )}

      {phase === 'results' && insights && (
        <EssayResultsDisplay 
          insights={insights} 
          interviewType="moments_that_matter"
        />
      )}
    </div>
  );
}
